import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  HStack,
  Link,
  IconButton,
  useDisclosure,
  Stack,
  Text,
} from '@chakra-ui/react';
import { HamburgerIcon, CloseIcon } from '@chakra-ui/icons';
import { FaLinkedin, FaGithub } from 'react-icons/fa';

const TypewriterLogo = () => {
  const [text, setText] = useState('');
  const phrases = ['Ramil Sharapov', 'Applications Architect', 'Full Stack Developer'];

  useEffect(() => {
    let phraseIndex = 0;
    let charIndex = 0;
    let isDeleting = false;
    let timeout;

    const type = () => {
      const currentPhrase = phrases[phraseIndex];

      if (!isDeleting) {
        // Typing forward
        setText(currentPhrase.substring(0, charIndex + 1));
        charIndex++;

        if (charIndex === currentPhrase.length) {
          // Finished typing, pause then delete
          isDeleting = true;
          timeout = setTimeout(type, 2000);
          return;
        }
      } else {
        // Deleting backward
        setText(currentPhrase.substring(0, charIndex - 1));
        charIndex--;

        if (charIndex === 0) {
          // Finished deleting, move to next phrase
          isDeleting = false;
          phraseIndex = (phraseIndex + 1) % phrases.length;
        }
      }

      timeout = setTimeout(type, isDeleting ? 50 : 100);
    };

    timeout = setTimeout(type, 500);

    return () => clearTimeout(timeout);
  }, []);

  return (
    <Text
      fontSize="xl"
      fontWeight="bold"
      color="brand.teal"
      minW="250px"
      maxW="600px"
      whiteSpace="nowrap"
      overflow="hidden"
    >
      {text}
      <Box as="span" animation="blink 1s infinite" ml="2px">
        |
      </Box>
    </Text>
  );
};

const Navigation = () => {
  const { isOpen, onToggle } = useDisclosure();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'About', href: '#about' },
    { name: 'Skills', href: '#skills' },
    { name: 'Projects', href: '#projects' },
    { name: 'Contact', href: '#contact' },
  ];

  return (
    <Box
      as="nav"
      position="fixed"
      top={0}
      left={0}
      right={0}
      zIndex={1000}
      bg={scrolled ? 'white' : 'transparent'}
      boxShadow={scrolled ? 'md' : 'none'}
      transition="all 0.3s ease"
      py={4}
    >
      <Flex
        maxW="1200px"
        mx="auto"
        px={8}
        align="center"
        justify="space-between"
      >
        {/* Logo */}
        <TypewriterLogo />

        {/* Desktop Navigation */}
        <HStack
          as="nav"
          spacing={8}
          display={{ base: 'none', md: 'flex' }}
        >
          {navLinks.map((link) => (
            <Link
              key={link.name}
              href={link.href}
              fontSize="lg"
              fontWeight="600"
              color={scrolled ? 'gray.700' : 'white'}
              position="relative"
              _hover={{
                textDecoration: 'none',
                _after: {
                  width: '100%',
                },
              }}
              _after={{
                content: '""',
                position: 'absolute',
                bottom: '-8px',
                left: 0,
                width: 0,
                height: '6px',
                bgGradient: 'linear(to-r, brand.teal, brand.blue, #00d9a3)',
                borderRadius: 'full',
                transition: 'width 0.3s ease',
                boxShadow: '0 3px 12px rgba(93, 196, 184, 0.5)',
              }}
            >
              {link.name}
            </Link>
          ))}
        </HStack>

        {/* Social Icons */}
        <HStack spacing={4} display={{ base: 'none', md: 'flex' }}>
          <IconButton
            as="a"
            href="https://www.linkedin.com/in/ramil-sharapov/"
            target="_blank"
            icon={<FaLinkedin size="24px" />}
            variant="ghost"
            colorScheme="blue"
            size="lg"
            rounded="full"
            bg="linear-gradient(135deg, #5BC0EB 0%, #69c7bc 100%)"
            color="white"
            _hover={{
              transform: 'scale(1.1)',
            }}
          />
          <IconButton
            as="a"
            href="https://github.com/Ramil1988"
            target="_blank"
            icon={<FaGithub size="24px" />}
            variant="ghost"
            colorScheme="gray"
            size="lg"
            rounded="full"
            bg="linear-gradient(135deg, #5BC0EB 0%, #69c7bc 100%)"
            color="white"
            _hover={{
              transform: 'scale(1.1)',
            }}
          />
        </HStack>

        {/* Mobile Menu Button */}
        <IconButton
          display={{ base: 'flex', md: 'none' }}
          onClick={onToggle}
          icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
          variant="ghost"
          aria-label="Toggle Navigation"
        />
      </Flex>

      {/* Mobile Menu */}
      {isOpen && (
        <Box
          pb={4}
          display={{ base: 'block', md: 'none' }}
          bg="white"
          boxShadow="md"
        >
          <Stack as="nav" spacing={4} px={8} pt={4}>
            {navLinks.map((link) => (
              <Link
                key={link.name}
                href={link.href}
                fontSize="lg"
                fontWeight="600"
                color="gray.700"
                onClick={onToggle}
              >
                {link.name}
              </Link>
            ))}
            <HStack spacing={4} pt={4}>
              <IconButton
                as="a"
                href="https://www.linkedin.com/in/ramil-sharapov/"
                target="_blank"
                icon={<FaLinkedin />}
                colorScheme="blue"
                rounded="full"
              />
              <IconButton
                as="a"
                href="https://github.com/Ramil1988"
                target="_blank"
                icon={<FaGithub />}
                colorScheme="gray"
                rounded="full"
              />
            </HStack>
          </Stack>
        </Box>
      )}

      <style>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      `}</style>
    </Box>
  );
};

export default Navigation;
